/**
 * PWAInstallNudge — soft chip that appears after a visitor has viewed
 * three FoodPulse pages, inviting them to add the app to their home
 * screen. Complements the existing `PwaInstallPrompt` (which is a
 * bigger, session-scoped call-to-action on the /install landing).
 *
 * Rules:
 *   • Only shown inside FoodPulse (URL prefix /food-pulse) so we don't
 *     nudge SocialPulse or IFT visitors.
 *   • Only shown once the visitor has viewed >= 3 distinct pages in
 *     the current session (counted via localStorage).
 *   • Dismissible; once dismissed, we won't nag again for 30 days.
 *   • On Android/Chrome, tapping "Install" fires the deferred
 *     `beforeinstallprompt` event. On iOS, we show the "Tap Share →
 *     Add to Home Screen" instruction card.
 *   • Standalone-installed instances (display-mode: standalone) never
 *     see the chip.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Share, Home } from "lucide-react";

const KEY_VIEWS      = "fp_pwa_views_v1";
const KEY_DISMISSED  = "fp_pwa_nudge_dismissed_v1";
const KEY_ACCEPTED   = "fp_pwa_nudge_installed_v1";
const THRESHOLD      = 3;
const DISMISS_DAYS   = 30;

function isStandalone() {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

function isIos() {
  try {
    const ua = window.navigator.userAgent || "";
    return /iP(hone|ad|od)/.test(ua) && !/CriOS|FxiOS/.test(ua);
  } catch {
    return false;
  }
}

function dismissedRecently() {
  try {
    const raw = localStorage.getItem(KEY_DISMISSED);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function bumpViewCount(pathname) {
  try {
    const raw = localStorage.getItem(KEY_VIEWS);
    const state = raw ? JSON.parse(raw) : { paths: [], total: 0 };
    // De-dupe consecutive same-page reloads.
    if (state.paths[state.paths.length - 1] !== pathname) {
      state.paths.push(pathname);
      state.total = (state.total || 0) + 1;
    }
    // Keep the tail small — we only care about the count.
    state.paths = state.paths.slice(-10);
    localStorage.setItem(KEY_VIEWS, JSON.stringify(state));
    return state.total;
  } catch {
    return 0;
  }
}

export default function PWAInstallNudge() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showIosCard, setShowIosCard] = useState(false);

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      try { localStorage.setItem(KEY_ACCEPTED, String(Date.now())); } catch {}
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    // Only meaningful on FoodPulse routes.
    if (!/^\/(food-pulse|basket|pantry|receipts|smart-basket)(\/|$)/.test(pathname)) {
      setVisible(false);
      return;
    }
    if (isStandalone()) return;
    if (dismissedRecently()) return;
    const total = bumpViewCount(pathname);
    if (total >= THRESHOLD) {
      // Small delay so the chip doesn't fight with above-the-fold content.
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, [pathname]);

  const dismiss = () => {
    try { localStorage.setItem(KEY_DISMISSED, String(Date.now())); } catch {}
    setVisible(false);
    setShowIosCard(false);
  };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") {
          try { localStorage.setItem(KEY_ACCEPTED, String(Date.now())); } catch {}
        }
      } catch {
        /* non-fatal */
      }
      setDeferred(null);
      setVisible(false);
      return;
    }
    // iOS Safari — show manual instructions.
    setShowIosCard(true);
  };

  if (!visible) return null;

  if (showIosCard) {
    return (
      <div
        className="fixed inset-x-3 bottom-3 z-[9997] sm:inset-x-auto sm:right-4 sm:max-w-sm"
        data-testid="fp-pwa-nudge-ios"
      >
        <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                Add FoodPulse
              </div>
              <div className="mt-1 text-sm text-slate-700">
                Tap the <b>Share</b> button <Share className="inline h-3.5 w-3.5" /> in
                Safari's toolbar, then choose <b>Add to Home Screen</b>{" "}
                <Home className="inline h-3.5 w-3.5" />.
              </div>
            </div>
            <button
              onClick={dismiss}
              data-testid="fp-pwa-nudge-close"
              aria-label="Close"
              className="rounded-full p-1 text-slate-400 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[9997] sm:inset-x-auto sm:right-4 sm:max-w-sm"
      data-testid="fp-pwa-nudge"
    >
      <div className="flex items-center gap-3 rounded-full border border-emerald-200 bg-white p-2 pl-3 shadow-xl ring-1 ring-black/5">
        <img
          src="/foodpulse-logo-round.png"
          alt=""
          width="36"
          height="36"
          className="h-9 w-9 shrink-0 rounded-full ring-1 ring-emerald-100"
        />
        <div className="min-w-0 flex-1 text-xs sm:text-sm">
          <div className="font-semibold text-emerald-800">
            Add FoodPulse to your home screen
          </div>
          <div className="text-slate-500">One tap. Works offline. No app store.</div>
        </div>
        <button
          onClick={install}
          data-testid="fp-pwa-nudge-install"
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
        >
          <Download className="h-3.5 w-3.5" />
          {isIos() ? "How" : "Install"}
        </button>
        <button
          onClick={dismiss}
          data-testid="fp-pwa-nudge-close"
          aria-label="Not now"
          className="shrink-0 rounded-full p-1 text-slate-400 hover:bg-slate-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
